import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  PlusIcon, 
  PuzzlePieceIcon, 
  RectangleStackIcon, 
  SparklesIcon,
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  PencilIcon,
  TrashIcon,
  PlayIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import quizService, { QuizListItem } from '../services/quizService';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { isUpmindyAndroidWebView } from '../utils/deviceDetection';
import LoadingSpinner from '../components/LoadingSpinner';
import QuizCreationModal from '../components/QuizCreationModal';
import UpgradePrompt from '../components/UpgradePrompt';
import UpgradeOptionsModal from '../components/UpgradeOptionsModal';
import ModuleIntroModal from '../components/ModuleIntroModal';
import { t } from '../utils/translations';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface Quiz {
  _id: string;
  title: string;
  description?: string;
  deckId?: string;
  deckTitle?: string;
  questionsCount: number;
  type: 'deck-based' | 'custom';
  createdAt: string;
  updatedAt: string;
  attemptsCount: number;
  lastAttempted?: string;
  averageScore: number;
  bestScore: number;
}

const Quizzes: React.FC = () => {
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { subscription, checkLimit } = useSubscription();
  const isAndroidWebView = isUpmindyAndroidWebView();
  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingQuizId, setDeletingQuizId] = useState<string | null>(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [showIntroModal, setShowIntroModal] = useState(false);

  // Check if we should show the intro modal on first visit
  useEffect(() => {
    const hasSeenIntro = localStorage.getItem('quizzes_intro_seen');
    if (!hasSeenIntro) {
      setShowIntroModal(true);
    }
  }, []);

  const handleCloseIntroModal = () => {
    localStorage.setItem('quizzes_intro_seen', 'true');
    setShowIntroModal(false);
  };
  const [editingQuiz, setEditingQuiz] = useState<QuizListItem | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const fetchQuizzes = async () => {
    try {
      setLoading(true);
      console.log('Fetching quizzes from API...');
      const fetchedQuizzes = await quizService.getQuizzes();
      console.log('Fetched quizzes:', fetchedQuizzes);
      setQuizzes(fetchedQuizzes);
    } catch (error) {
      console.error('Error fetching quizzes:', error);
      // Only use mock data if there's actually an error
      console.log('Using mock data as fallback');
      setQuizzes([
        {
          _id: '1',
          title: 'Quiz de JavaScript',
          description: 'Teste seus conhecimentos sobre JavaScript',
          type: 'custom',
          questionsCount: 10,
          tags: ['javascript', 'frontend'],
          createdAt: '2024-01-15T10:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z',
          attemptsCount: 5,
          lastAttempted: '2024-01-20T15:30:00Z',
          averageScore: 85,
          bestScore: 95,
          reviewCount: 5,
          nextReview: new Date().toISOString(),
          easeFactor: 2.5,
          interval: 3
        },
        {
          _id: '2',
          title: 'Quiz de Node.js',
          description: 'Conceitos básicos e avançados de Node.js',
          type: 'deck-based',
          deckId: 'deck1',
          deckTitle: 'Deck Node.js',
          questionsCount: 15,
          tags: ['nodejs', 'backend'],
          createdAt: '2024-01-18T14:00:00Z',
          updatedAt: '2024-01-18T14:00:00Z',
          attemptsCount: 3,
          averageScore: 78,
          bestScore: 88,
          reviewCount: 3,
          nextReview: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString(),
          easeFactor: 2.3,
          interval: 2
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch quizzes after authentication is complete
    if (!authLoading) {
      fetchQuizzes();
    }
  }, [authLoading]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleStartQuiz = (quizId: string) => {
    // Navigate to quiz playing page
    navigate(`/quiz/${quizId}`);
  };

  const handleEditQuizModal = (quiz: QuizListItem) => {
    setEditingQuiz(quiz);
    setEditTitle(quiz.title);
    setEditDescription(quiz.description || '');
    setEditTags(quiz.tags || []);
    setNewTag('');
  };

  const handleEditQuizPage = (quizId: string) => {
    navigate(`/quizzes/management/${quizId}`);
  };

  const handleCloseEditModal = () => {
    setEditingQuiz(null);
    setEditTitle('');
    setEditDescription('');
    setEditTags([]);
    setNewTag('');
  };

  const handleSaveEdit = async () => {
    if (!editingQuiz) return;

    if (!editTitle.trim()) {
      toast.error('O título não pode estar vazio');
      return;
    }

    try {
      setSaving(true);
      await quizService.updateQuiz(editingQuiz._id, {
        title: editTitle,
        description: editDescription,
        tags: editTags,
      });
      
      toast.success('Quiz atualizado com sucesso!');
      handleCloseEditModal();
      fetchQuizzes();
    } catch (error) {
      console.error('Error updating quiz:', error);
      toast.error('Erro ao atualizar quiz');
    } finally {
      setSaving(false);
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !editTags.includes(newTag.trim())) {
      setEditTags([...editTags, newTag.trim()]);
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setEditTags(editTags.filter(tag => tag !== tagToRemove));
  };

  const handleDeleteQuiz = async (quizId: string) => {
    try {
      await quizService.deleteQuiz(quizId);
      setQuizzes(quizzes.filter(quiz => quiz._id !== quizId));
      setDeletingQuizId(null);
      // toast.success('Quiz deleted successfully');
    } catch (error) {
      console.error('Error deleting quiz:', error);
      // toast.error('Failed to delete quiz');
    }
  };

  const filteredQuizzes = quizzes.filter(quiz =>
    quiz.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quiz.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading || authLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50/30 to-pink-50 dark:from-gray-900 dark:via-purple-900/10 dark:to-pink-900/20 transition-all duration-500">
      <div className="max-w-6xl mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 dark:from-purple-400 dark:via-purple-300 dark:to-pink-400 bg-clip-text text-transparent">
              Quizzes
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
              Crie e gerencie seus quizzes para testar conhecimentos
            </p>
            {subscription && subscription.subscriptionTier === 'free' && !isAndroidWebView && (
              <div className="flex items-center space-x-2 text-sm">
                <span className={`font-medium ${quizzes.length >= 3 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                  {quizzes.length}/3 quizzes no plano gratuito
                </span>
                <Link 
                  to="/pricing"
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-semibold underline transition-colors duration-200"
                >
                  Upgrade
                </Link>
              </div>
            )}
          </div>
          <div className="mt-6 sm:mt-0">
            <button
              onClick={() => {
                if (!checkLimit('quiz', quizzes.length)) {
                  if (isAndroidWebView) {
                    // Show toast notification instead of modal
                    toast.error('Faça upgrade para criar mais do que 3 quizzes.', {
                      duration: 4000,
                      position: 'top-center',
                      style: {
                        background: '#EF4444',
                        color: '#fff',
                        fontWeight: 'bold',
                      },
                    });
                    return;
                  } else {
                    setShowUpgradePrompt(true);
                    return;
                  }
                }
                setShowQuizModal(true);
              }}
              className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-purple-500 via-pink-600 to-purple-600 hover:from-purple-600 hover:via-pink-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-purple-500/25 hover:shadow-purple-600/30"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar Quiz
            </button>
          </div>
        </div>

        {/* Search - Only show if there are at least 3 quizzes */}
        {quizzes.length >= 3 && (
          <div className="relative mt-8 mb-6">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="block w-full pl-12 pr-4 py-4 border border-gray-200/50 dark:border-gray-700/50 rounded-2xl leading-5 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 dark:focus:ring-purple-400/50 dark:focus:border-purple-400/50 transition-all duration-300 shadow-lg dark:shadow-xl hover:shadow-xl dark:hover:shadow-2xl hover:bg-white dark:hover:bg-gray-800 text-lg"
            />
          </div>
        )}

        {/* Quizzes Grid */}
        {filteredQuizzes.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-lg border border-gray-200 dark:border-gray-700">
            <PuzzlePieceIcon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {searchTerm ? 'Nenhum quiz encontrado' : 'Você ainda não tem quizzes'}
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm 
                ? 'Tente ajustar os termos de busca' 
                : 'Comece criando seu primeiro quiz'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={() => setShowQuizModal(true)}
                  className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-sm font-semibold rounded-xl shadow-lg text-white bg-gradient-to-r from-purple-500 via-pink-600 to-purple-600 hover:from-purple-600 hover:via-pink-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-900 transform hover:scale-105 transition-all duration-200 backdrop-blur-sm shadow-purple-500/25 hover:shadow-purple-600/30"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Criar Primeiro Quiz
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 mt-8">
            {filteredQuizzes.map((quiz) => (
              <div
                key={quiz._id}
                className="group bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden shadow-xl rounded-2xl hover:shadow-2xl dark:hover:shadow-2xl dark:hover:shadow-gray-900/50 transition-all duration-300 hover:-translate-y-2 hover:scale-105 hover:border-purple-300/50 dark:hover:border-purple-500/50"
              >
                {/* Card Header with gradient overlay */}
                <div className="relative bg-gradient-to-br from-purple-50 via-pink-50 to-purple-50 dark:from-gray-800 dark:via-purple-900/20 dark:to-pink-900/20 p-6 border-b border-gray-100/50 dark:border-gray-700/50">
                  <div className="absolute inset-0 bg-gradient-to-br from-transparent via-purple-500/5 to-pink-500/10 dark:from-transparent dark:via-purple-400/10 dark:to-pink-400/10"></div>
                  <div className="relative flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${
                        quiz.type === 'deck-based' 
                          ? 'bg-blue-100 dark:bg-blue-900/30' 
                          : 'bg-purple-100 dark:bg-purple-900/30'
                      }`}>
                        {quiz.type === 'deck-based' ? (
                          <RectangleStackIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        ) : (
                          <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                        )}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        quiz.type === 'deck-based'
                          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                      }`}>
                        {quiz.type === 'deck-based' ? 'Baseado em Deck' : 'Personalizado'}
                      </span>
                    </div>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                    {quiz.title}
                  </h3>
                  
                  {quiz.description && (
                    <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed mt-2">
                      {quiz.description}
                    </p>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-6">
                  {quiz.deckTitle && (
                    <div className="flex items-center text-sm text-blue-600 dark:text-blue-400 mb-4 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-lg">
                      <RectangleStackIcon className="h-4 w-4 mr-2" />
                      <span className="font-medium">Deck: {quiz.deckTitle}</span>
                    </div>
                  )}

                  <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 space-x-6 mb-4">
                    <div className="flex items-center bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded-lg">
                      <PuzzlePieceIcon className="h-4 w-4 mr-2 text-purple-600 dark:text-purple-400" />
                      <span className="font-medium">{quiz.questionsCount} perguntas</span>
                    </div>
                  </div>

                  {/* Stats */}
                  {quiz.attemptsCount > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gradient-to-br from-gray-50 to-purple-50/50 dark:from-gray-700/50 dark:to-purple-900/20 rounded-lg border border-gray-200/50 dark:border-gray-600/50">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tentativas</p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{quiz.attemptsCount}</p>
                      </div>
                      <div className="text-center border-x border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Média</p>
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{quiz.averageScore}%</p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Melhor</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{quiz.bestScore}%</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-row gap-3">
                    <button
                      onClick={() => quiz.questionsCount === 0 ? handleEditQuizPage(quiz._id) : handleStartQuiz(quiz._id)}
                      className="flex-1 inline-flex justify-center items-center px-4 py-3 border border-transparent text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-purple-500 via-pink-500 to-purple-600 hover:from-purple-600 hover:via-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 dark:focus:ring-offset-gray-800 transition-all duration-200 hover:scale-105 shadow-lg shadow-purple-500/25"
                    >
                      {quiz.questionsCount === 0 ? (
                        <>
                          <PencilIcon className="h-4 w-4 mr-2" />
                          Criar questões
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4 mr-2" />
                          Jogar
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => handleEditQuizPage(quiz._id)}
                      className="inline-flex justify-center items-center px-4 py-3 border border-gray-200 dark:border-gray-600 text-sm font-semibold rounded-xl text-gray-700 dark:text-gray-200 bg-white/80 dark:bg-gray-700/80 backdrop-blur-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 shadow-lg shadow-gray-500/25 transition-all duration-200 hover:scale-105"
                    >
                      <Cog6ToothIcon className="h-4 w-4 mr-2" />
                      Gerenciar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {deletingQuizId && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
            <div className="relative top-20 mx-auto p-5 border border-gray-200 dark:border-gray-700 w-96 shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-md bg-white dark:bg-gray-800">
              <div className="mt-3 text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                  <TrashIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mt-4">
                  Deletar Quiz?
                </h3>
                <div className="mt-2 px-7 py-3">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tem certeza que deseja deletar este quiz? Esta ação não pode ser desfeita.
                  </p>
                </div>
                <div className="flex justify-center space-x-3 mt-4">
                  <button
                    onClick={() => setDeletingQuizId(null)}
                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 text-base font-medium rounded-md shadow-sm hover:bg-gray-400 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-gray-600 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={() => handleDeleteQuiz(deletingQuizId)}
                    className="px-4 py-2 bg-red-600 dark:bg-red-700 text-white text-base font-medium rounded-md shadow-sm hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-offset-gray-800 transition-colors duration-200"
                  >
                    Deletar
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Edit Quiz Modal */}
        {editingQuiz && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-75 overflow-y-auto h-full w-full z-50 backdrop-blur-sm">
            <div className="relative top-20 mx-auto p-6 border border-gray-200 dark:border-gray-700 max-w-2xl shadow-lg dark:shadow-2xl dark:shadow-black/50 rounded-xl bg-white dark:bg-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                  Editar Quiz
                </h3>
                <button
                  onClick={handleCloseEditModal}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Título
                  </label>
                  <input
                    type="text"
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                    placeholder="Título do quiz"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Descrição
                  </label>
                  <textarea
                    value={editDescription}
                    onChange={(e) => setEditDescription(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all resize-none"
                    placeholder="Descrição do quiz (opcional)"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {editTags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300"
                      >
                        {tag}
                        <button
                          onClick={() => handleRemoveTag(tag)}
                          className="ml-2 text-purple-600 dark:text-purple-400 hover:text-purple-800 dark:hover:text-purple-200 transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:border-transparent transition-all"
                      placeholder="Adicionar tag"
                    />
                    <button
                      onClick={handleAddTag}
                      className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!newTag.trim()}
                    >
                      Adicionar
                    </button>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleCloseEditModal}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={saving || !editTitle.trim()}
                >
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upgrade Prompt */}
        {showUpgradePrompt && (
          <UpgradeOptionsModal
            message="Você atingiu o limite de 3 quizzes no plano gratuito."
            feature="quizzes ilimitados"
            onClose={() => setShowUpgradePrompt(false)}
          />
        )}

        {/* Module Intro Modal */}
        <ModuleIntroModal
          isOpen={showIntroModal}
          onClose={handleCloseIntroModal}
          moduleType="quizzes"
        />

        {/* Quiz Creation Modal */}
        <QuizCreationModal
          isOpen={showQuizModal}
          onClose={() => setShowQuizModal(false)}
          onQuizCreated={fetchQuizzes}
          onUpgradePrompt={() => setShowUpgradePrompt(true)}
        />
      </div>
    </div>
  );
};

export default Quizzes;